/**
 * The start-workout flow's words, shared so the web sheet and the mobile card
 * say the same thing.
 *
 * The endpoint sends reason `kind`s as KEYS, never as copy, so every word here
 * is the client's. Sentences are runs of `StartCopySegment`: the muscles a
 * sentence turns on are `strong` and its numbers are `figure`s, and each
 * platform sets those in its own emphasis — no markup in shared.
 *
 * A pick's justification is layered, always: a plain sentence a beginner reads,
 * and a receipt under it carrying the rule, each muscle's own number and how a
 * set was credited, for a sceptic to check. Every number comes from the payload
 * — the same credited sets the reports read — so the flow cannot disagree with
 * the rest of the app, or with itself.
 */

import {
  assistingCaution,
  daysFromHours,
  lastDoneLabel,
  leadingMuscleNames,
  muscleNames,
  promote,
  sessionMinutes,
  typicalMinutes,
  weekTally,
} from './start-frame';
import { formatDisplayDate } from '../../date';
import { MUSCLE_LABELS } from '../../muscle-map';

import type { StartWorkoutFrame, StartWorkoutStep } from './start-frame';
import type { ID } from '../../../types/common/id.types';
import type {
  StartOptionsAvoid,
  StartOptionsCompletedToday,
  StartOptionsDay,
  StartOptionsMissed,
  StartOptionsNextDue,
  StartOptionsSuggestion,
  StartOptionsSuggestionReason,
  StartOptionsWorkout,
  StartWorkoutOptions,
} from '../../../types/domain/start-options.types';
import type { DateInput } from '../../date';
import type { MuscleId } from '../../muscle-map';
import type { CopySegment } from '../../text';

// =============================================================================
// SEGMENTS
// =============================================================================

/** A run of the flow's copy. Only the week's missed count takes a tone. */
export interface StartCopySegment extends CopySegment {
  tone?: 'warning';
}

const plain = (text: string): StartCopySegment => ({ text });
const strong = (text: string): StartCopySegment => ({ text, strong: true });
const figure = (value: number | string): StartCopySegment => ({
  text: String(value),
  figure: true,
});

// =============================================================================
// FIXED COPY
// =============================================================================

/** The flow's fixed strings — everything that is not built from the payload. */
export const START_WORKOUT_COPY = {
  pickerTitle: 'All workouts',
  errorTitle: "Couldn't work out what you could start",
  retry: 'Try again',
  emptyTitle: 'Create a workout and you can start it here.',
  emptyDescription:
    "Once you've made one, the lightning button at the bottom will start it.",
  createWorkout: 'Create a workout',
  chooseWorkout: 'Choose a workout',
  pickToStart: 'Pick a workout to start',
  restTitle: 'Your plan has you resting today.',
  restBody: 'You can still train. It logs as an extra session.',
  weekLabel: 'This week',
  catchupSuffix: 'today is a rest day',
  fetchFailed: 'Unable to work out what you could start.',
} as const;

// =============================================================================
// CHROME
// =============================================================================

/** The header's title for a frame, or for the picker behind its hatch. */
export function startWorkoutTitle(
  frame: StartWorkoutFrame,
  step: StartWorkoutStep
): string {
  if (step === 'picker') return START_WORKOUT_COPY.pickerTitle;

  switch (frame.kind) {
    case 'loading':
      return 'Finding your workout…';
    case 'empty':
      return 'No workouts yet';
    case 'scheduled':
      return "Today's workout";
    case 'done':
    case 'extra':
      return 'Done for today';
    case 'catchup': {
      const weekday = formatDisplayDate(frame.missed.due, 'weekday');
      return weekday ? `${weekday}'s session` : 'Missed session';
    }
    case 'rest':
      return 'Rest day';
    default:
      return 'Start a workout';
  }
}

/**
 * The CTA names the pick, so a mistaken tap costs nothing. Unpicked, it is an
 * instruction rather than an "off" sign.
 */
export function startCtaLabel(workout: StartOptionsWorkout | null): string {
  return workout ? `Start ${workout.name}` : START_WORKOUT_COPY.pickToStart;
}

/** The escape hatch under the CTA: the whole library, counted. */
export function hatchLabel(options: StartWorkoutOptions): string {
  return `All workouts · ${options.workouts.length}`;
}

/**
 * The pill offering a fresher answer that landed while an older one was on
 * screen. Named for what changed, so the user knows whether to look.
 */
export function latePillLabel(options: StartWorkoutOptions): string {
  return `${options.plan ? "Today's plan is ready" : 'Suggestions ready'} · Show`;
}

/** The staleness line over a remembered answer: how old the facts are. */
export function staleLine(fetchedAt: DateInput): StartCopySegment[] {
  return [
    strong('Couldn’t connect'),
    plain(' — showing what the app knew at '),
    figure(formatDisplayDate(fetchedAt, 'time') ?? ''),
    plain('. You can still start a workout.'),
  ];
}

// =============================================================================
// THE PLAN AND THE WEEK
// =============================================================================

/**
 * The plan line above the ribbon. `week`/`of` are dropped together or not at
 * all — the backend nulls both when the plan's length is unknown.
 */
export function planLine(
  options: StartWorkoutOptions,
  suffix?: string
): StartCopySegment[] | null {
  if (!options.plan) return null;
  const { plan } = options;
  const hasWeeks = plan.week !== null && plan.of !== null;

  return [
    ...(hasWeeks ? [plain(`Week ${plan.week} of ${plan.of} · `)] : []),
    strong(plan.name),
    ...(suffix ? [plain(` · ${suffix}`)] : []),
  ];
}

/**
 * The counts beside the ribbon, as lines: "1 of 4 done", then the extras and
 * the misses on a second line when there are any.
 */
export function weekTallyLines(
  days: readonly StartOptionsDay[]
): StartCopySegment[][] {
  const { done, planned, extra, missed } = weekTally(days);
  const lines: StartCopySegment[][] = [
    [figure(done), plain(' of '), figure(planned), plain(' done')],
  ];

  const second: StartCopySegment[] = [];
  if (extra > 0) second.push(figure(extra), plain(' extra'));
  if (missed > 0) {
    if (second.length > 0) second.push(plain(' · '));
    second.push({ text: `${missed} missed`, tone: 'warning' });
  }
  if (second.length > 0) lines.push(second);

  return lines;
}

/** One narrow weekday under each dot. */
export function dayLetter(day: StartOptionsDay): string {
  return formatDisplayDate(day.date, 'weekdayNarrow') ?? '';
}

/** What a day's dot says to a screen reader. */
export function dayLabel(day: StartOptionsDay): string {
  const date = formatDisplayDate(day.date, 'weekdayDate') ?? day.date;
  const extra =
    day.extra > 0
      ? `, ${day.extra} extra ${day.extra === 1 ? 'session' : 'sessions'}`
      : '';

  const state = {
    done: 'done',
    missed: 'missed',
    due: 'due',
    rest: 'rest day',
  }[day.status];

  return `${date}, ${state}${extra}`;
}

/** The label over the next scheduled session. */
export function nextUpLabel(nextDue: StartOptionsNextDue): string {
  return `Next up · ${formatDisplayDate(nextDue.due, 'weekday') ?? ''}`;
}

/** Which muscles were steered clear of, and why — so a gap is explained. */
export function avoidLine(options: StartWorkoutOptions): string | null {
  const { sore_today: sore, trained_hard: hard } = options.avoid;
  if (sore.length === 0 && hard.length === 0) return null;

  const clauses: string[] = [];
  if (sore.length > 0) {
    clauses.push(`${muscleNames(sore)} — sore in today's check-in`);
  }
  if (hard.length > 0) {
    clauses.push(`${muscleNames(hard)} — trained hard inside the window`);
  }

  return `Steering clear of ${clauses.join('; ')}.`;
}

/** A session logged today, reported back on the calm card. */
export function sessionLine(
  session: StartOptionsCompletedToday,
  isExtra: boolean
): StartCopySegment[] {
  return [
    figure(sessionMinutes(session)),
    plain(' min · finished at '),
    figure(formatDisplayDate(session.completed_at, 'time') ?? ''),
    plain(
      isExtra
        ? '. An extra session this week — the plan keeps its own count.'
        : ' — today’s plan session.'
    ),
  ];
}

// =============================================================================
// A WORKOUT'S LINES
// =============================================================================

/** The card's sub-line: "about 45 min · 6 exercises · last done 5 days ago". */
export function cardMeta(workout: StartOptionsWorkout): StartCopySegment[] {
  const minutes = typicalMinutes(workout);

  return [
    ...(minutes !== null
      ? [plain('about '), figure(minutes), plain(' min · ')]
      : []),
    figure(workout.exercises),
    plain(
      ` exercises · ${workout.last_completed_at ? 'last done ' : ''}${lastDoneLabel(workout)}`
    ),
  ];
}

/** A row's sub-line: "~45 min · 6 exercises · 5 days ago". */
export function rowMeta(workout: StartOptionsWorkout): StartCopySegment[] {
  const minutes = typicalMinutes(workout);

  return [
    ...(minutes !== null
      ? [plain('~'), figure(minutes), plain(' min · ')]
      : []),
    figure(workout.exercises),
    plain(` exercises · ${lastDoneLabel(workout)}`),
  ];
}

/** The figure's accessible name. */
export function figureTitle(workout: StartOptionsWorkout): string {
  return `Muscles worked by ${workout.name}`;
}

// =============================================================================
// THE PICK'S REASON
// =============================================================================

export type PickTagTone = 'accent' | 'warning';

/** What a pick's justification says, once the reason has been read. */
export interface PickReasonView {
  /** The tag naming the situation — "Best match", "Today", "Missed". */
  tag?: string;
  /** Warning-toned tag, for a session that was missed. */
  tagTone?: PickTagTone;
  /** The reason's own kind, given words: "No set this week", "Fewest sets". */
  kind?: string;
  /** The plain sentence a beginner reads. */
  sentence: StartCopySegment[];
  /** The mono-numbered line a sceptic checks. */
  receipt?: StartCopySegment[];
  /** One line saying which lightly-worked muscles the avoid list named. */
  caution?: StartCopySegment[];
  /**
   * A glyph beside the sentence, named rather than drawn so each platform
   * picks its own.
   *
   * Given only where the icon already carries this exact meaning elsewhere in
   * the app — a calendar for a date, and nothing else so far. A suggestion's
   * reason has none: no single glyph is true of both a coverage gap and a
   * volume band, and the `kind` tag above already names which rule produced
   * the pick.
   */
  icon?: 'calendar';
}

/** An alternative beneath the card, compact. */
export interface PickRowView {
  workout: StartOptionsWorkout;
  tag?: string;
  tagTone?: PickTagTone;
  /** A short reason, one line. */
  note?: string;
  /** The one-clause warn-not-exclude line. */
  caution?: string;
}

/** Today's scheduled session. */
export function scheduledReason(): PickReasonView {
  return {
    tag: 'Today',
    tagTone: 'accent',
    icon: 'calendar',
    sentence: [plain('Today’s session on your plan.')],
  };
}

/**
 * A session missed earlier this week, offered on a day with nothing due. It
 * answers "does today count?" straight: a late make-up does not clear the miss.
 */
export function catchupReason(missed: StartOptionsMissed): PickReasonView {
  return {
    tag: ['Missed', formatDisplayDate(missed.due, 'weekdayDate')]
      .filter(Boolean)
      .join(' · '),
    tagTone: 'warning',
    sentence: [
      plain('Today logs as an '),
      strong('extra session'),
      plain(
        ` — ${formatDisplayDate(missed.due, 'weekday') ?? ''} stays missed.`
      ),
    ],
  };
}

// -----------------------------------------------------------------------------
// Coverage gap
// -----------------------------------------------------------------------------

function coverageSentence(
  reason: Extract<StartOptionsSuggestionReason, { kind: 'coverage_gap' }>
): StartCopySegment[] {
  const never = reason.muscles.filter((muscle) => muscle.hours_since === null);
  const known = reason.muscles.filter((muscle) => muscle.hours_since !== null);

  const longest = known.reduce(
    (worst, muscle) => Math.max(worst, muscle.hours_since ?? 0),
    0
  );

  // Phrased around "no credited set" rather than around the muscles, because
  // the labels are a mix of plural and singular nouns — "lats" and "lower back"
  // cannot share a verb, and picking one per reason would be a bug waiting for
  // the right payload.
  const segments: StartCopySegment[] = [plain('No credited set for your ')];

  if (never.length > 0) {
    segments.push(
      strong(muscleNames(never.map((muscle) => muscle.id))),
      plain(', ever')
    );
    if (known.length > 0) segments.push(plain(', or for your '));
  }

  if (known.length > 0) {
    segments.push(
      strong(muscleNames(known.map((muscle) => muscle.id))),
      plain(` in ${daysFromHours(longest)} days`)
    );
  }

  segments.push(plain(' — the guideline is every major group, twice a week.'));
  return segments;
}

function coverageReceipt(
  reason: Extract<StartOptionsSuggestionReason, { kind: 'coverage_gap' }>
): StartCopySegment[] {
  const segments: StartCopySegment[] = [];

  reason.muscles.forEach((muscle, index) => {
    if (index > 0) segments.push(plain(' · '));
    segments.push(plain(`${MUSCLE_LABELS[muscle.id].toLowerCase()}: `));

    if (muscle.hours_since === null) {
      segments.push(plain('never trained'));
      return;
    }

    segments.push(
      plain('last credited set '),
      figure(daysFromHours(muscle.hours_since)),
      plain(
        ` days ago${muscle.last_workout ? ` (${muscle.last_workout})` : ''}`
      )
    );
  });

  segments.push(
    plain(' · window '),
    figure(daysFromHours(reason.window_hours)),
    plain(' days · a set counts ×'),
    figure(reason.credit.target),
    plain(' as target, ×'),
    figure(reason.credit.assisting),
    plain(' assisting')
  );

  return segments;
}

// -----------------------------------------------------------------------------
// Low volume
// -----------------------------------------------------------------------------

function volumeSentence(
  reason: Extract<StartOptionsSuggestionReason, { kind: 'low_volume' }>
): StartCopySegment[] {
  return [
    plain('Of the muscles you trained, '),
    strong(muscleNames(reason.muscles.map((muscle) => muscle.id))),
    plain(` got the fewest sets in the last ${reason.window_days} days.`),
  ];
}

function volumeReceipt(
  reason: Extract<StartOptionsSuggestionReason, { kind: 'low_volume' }>
): StartCopySegment[] {
  const segments: StartCopySegment[] = [];

  reason.muscles.forEach((muscle, index) => {
    if (index > 0) segments.push(plain(' · '));
    segments.push(
      plain(`${MUSCLE_LABELS[muscle.id].toLowerCase()} `),
      figure(muscle.sets_per_week.toFixed(1))
    );
  });

  segments.push(
    plain(' sets a week · under '),
    figure(reason.band_floor),
    plain(', the bottom of the range studied · last '),
    figure(reason.window_days),
    plain(' days')
  );

  return segments;
}

// -----------------------------------------------------------------------------
// The warn-not-exclude line
// -----------------------------------------------------------------------------

/**
 * A pick the avoid list did not remove, but touches anyway.
 *
 * The endpoint excludes on primary muscles alone, because a half-credit signal
 * has no half-exclusion and matching assisting muscles would empty the flow on
 * most sore days. Rather than argue with that, the card says it in one line.
 */
export function cautionLine(
  workout: StartOptionsWorkout,
  avoid: StartOptionsAvoid
): StartCopySegment[] | undefined {
  const { sore, trainedHard } = assistingCaution(workout, avoid);
  if (sore.length === 0 && trainedHard.length === 0) return undefined;

  const segments: StartCopySegment[] = [plain('Works your ')];
  if (sore.length > 0) {
    segments.push(
      strong(muscleNames(sore)),
      plain(' lightly too — sore today')
    );
  }
  if (sore.length > 0 && trainedHard.length > 0) {
    segments.push(plain('; and your '));
  }
  if (trainedHard.length > 0) {
    segments.push(
      strong(muscleNames(trainedHard)),
      plain(' lightly too — trained hard this week')
    );
  }
  segments.push(plain('.'));

  return segments;
}

/**
 * The row-sized version of the card's warn-not-exclude line. Truncated to one
 * clause because the row ellipsises; the full sentence is on the card the row
 * promotes into.
 */
export function cautionText(
  workout: StartOptionsWorkout,
  avoid: StartOptionsAvoid
): string | undefined {
  const { sore, trainedHard } = assistingCaution(workout, avoid);

  if (sore.length > 0) return `Works ${muscleNames(sore)} lightly — sore`;
  if (trainedHard.length > 0) {
    return `Works ${muscleNames(trainedHard)} lightly — trained hard`;
  }
  return undefined;
}

/** A suggestion's reason in one line, for a compact row. */
export function shortReason(reason: StartOptionsSuggestionReason): string {
  const ids: MuscleId[] = reason.muscles.map((muscle) => muscle.id);

  if (reason.kind === 'low_volume') {
    return `${leadingMuscleNames(ids)}: fewest sets`;
  }

  const worst = reason.muscles[0];
  if (worst && worst.hours_since === null) {
    return `${leadingMuscleNames(ids)}: never trained`;
  }

  return `${leadingMuscleNames(ids)}: no set in ${daysFromHours(worst?.hours_since ?? 0)} days`;
}

/** Everything a suggestion's card needs, read off its reason. */
export function suggestionReasonView({
  reason,
  workout,
  avoid,
  isBest,
}: {
  reason: StartOptionsSuggestionReason;
  workout: StartOptionsWorkout;
  avoid: StartOptionsAvoid;
  /** Rank 1 wears the best-match tag wherever it sits. */
  isBest: boolean;
}): PickReasonView {
  return {
    tag: isBest ? 'Best match' : 'Your pick',
    tagTone: 'accent',
    kind: reason.kind === 'coverage_gap' ? 'No set this week' : 'Fewest sets',
    sentence:
      reason.kind === 'coverage_gap'
        ? coverageSentence(reason)
        : volumeSentence(reason),
    receipt:
      reason.kind === 'coverage_gap'
        ? coverageReceipt(reason)
        : volumeReceipt(reason),
    caution: cautionLine(workout, avoid),
  };
}

// =============================================================================
// THE FRAMES WITH ALTERNATIVES
// =============================================================================

/** A frame that offers a pick on a card with alternatives beneath it. */
export interface PickListView {
  /** The line above the card. */
  intro: string;
  workout: StartOptionsWorkout;
  reason: PickReasonView;
  rows: PickRowView[];
}

/**
 * An unscheduled plan's frame: its own workouts, longest gap first. The pick
 * keeps the "Next in plan" claim only while it is the first one; promoting
 * another moves the tag down onto the row it came from.
 */
export function planNextView(
  picks: StartOptionsWorkout[],
  selectedId: ID | null
): PickListView | null {
  const promoted = promote(picks, selectedId, (workout) => workout.id);
  if (!promoted) return null;

  const { chosen, rest } = promoted;
  const first = picks[0];
  const isFirst = chosen.id === first?.id;
  const runnerUp = rest[0];

  return {
    intro: `Your plan has no set days — any of its ${picks.length} will do.`,
    workout: chosen,
    reason: {
      tag: isFirst ? 'Next in plan' : 'Your pick',
      tagTone: 'accent',
      sentence: isFirst
        ? [plain('The one you’ve gone '), strong('longest without'), plain('.')]
        : [plain('Your pick from the plan.')],
      receipt: runnerUp ? [plain(`next longest: ${runnerUp.name}`)] : undefined,
    },
    rows: rest.map((workout) => ({
      workout,
      tag: workout.id === first?.id ? 'Next in plan' : undefined,
    })),
  };
}

/** No plan, and the analytics answered: the picks, best match first. */
export function suggestionsView(
  picks: StartOptionsSuggestion[],
  selectedId: ID | null,
  avoid: StartOptionsAvoid
): PickListView | null {
  const promoted = promote(picks, selectedId, (pick) => pick.workout.id);
  if (!promoted) return null;

  const { chosen, rest } = promoted;

  return {
    intro: `No plan running. ${picks.length} ${
      picks.length === 1 ? 'pick' : 'picks'
    } from the two-a-week guideline and your recent sets, best match first.`,
    workout: chosen.workout,
    reason: suggestionReasonView({
      reason: chosen.reason,
      workout: chosen.workout,
      avoid,
      isBest: chosen.rank === 1,
    }),
    rows: rest.map((pick) => ({
      workout: pick.workout,
      tag: pick.rank === 1 ? 'Best match' : undefined,
      note: shortReason(pick.reason),
      caution: cautionText(pick.workout, avoid),
    })),
  };
}

// =============================================================================
// FRAME → PICKER
// =============================================================================

/** A row's tag in the picker, keyed by the workout's id rendered as a string. */
export type PickerTags = Record<string, { label: string; tone: PickTagTone }>;

/**
 * The tag the picker carries in from the frame it was opened over, so the pick
 * arrives selected AND named — a thumb-slip onto the hatch costs one tap.
 */
export function pickerTags(frame: StartWorkoutFrame): PickerTags {
  const tag = (
    id: ID,
    label: string,
    tone: PickTagTone = 'accent'
  ): PickerTags => ({ [String(id)]: { label, tone } });

  switch (frame.kind) {
    case 'scheduled':
      return tag(frame.pick.id, 'Today');
    case 'catchup':
      return tag(frame.missed.workout.id, 'Missed', 'warning');
    case 'plan_next': {
      const first = frame.picks[0];
      return first ? tag(first.id, 'Next in plan') : {};
    }
    case 'suggestions': {
      const best = frame.picks.find((pick) => pick.rank === 1);
      return best ? tag(best.workout.id, 'Best match') : {};
    }
    default:
      return {};
  }
}

/**
 * Why the picker is standing in for a frame, when it is.
 *
 * `not_entitled` says nothing: the suggestions were never promised, and a line
 * explaining their absence would turn a working flow into an advert for a
 * tier. The bolt's job is to start a workout, and it still does.
 */
export function pickerNote(
  frame: StartWorkoutFrame,
  options: StartWorkoutOptions
): StartCopySegment[] | undefined {
  if (frame.kind !== 'picker' || frame.reason !== 'not_enough_data') {
    return undefined;
  }

  const after = options.suggestions_after;
  if (!after) return undefined;

  const remaining = Math.max(0, after.sessions_needed - after.sessions_logged);

  return [
    plain(
      `${remaining === 1 ? 'One more session' : `${remaining} more sessions`} this week and suggestions begin — a week counts once it has `
    ),
    strong(String(after.sessions_needed)),
    plain(' sessions, and this one has '),
    strong(String(after.sessions_logged)),
    plain('.'),
  ];
}

/** The picker's search field, counted. */
export function pickerSearchPlaceholder(options: StartWorkoutOptions): string {
  return `Search ${options.workouts.length} workouts`;
}

/** What an empty search says. */
export function noMatchesLine(query: string): string {
  return `Nothing matches “${query}”.`;
}
